/**
 * Compile a UI-side x-allOf rule into a BD-friendly plain-English narrative
 * for the read-only "Conditional Rules" section.
 *
 * Rule shape (see serialization.js):
 *   { ifConditions: [{ field, operator, value?, required }],
 *     thenFields:    [{ fieldName, required, ... }],
 *     elseFields:    [...],
 *     negated: boolean }
 *
 * Returns:
 *   {
 *     condition: 'If <field> equals <value> and <field> is at least <value>',
 *     then:      'block_zone_name is required'  OR
 *                'at least one of a, b must be provided'  OR
 *                { headline: '5 fields are required:', fields: ['a', 'b', ...] },
 *     elsePart:  'block_zone_name is optional' | null
 *   }
 *
 * The compiler stays purely data — rendering is the caller's job (so the
 * one-liner can choose to drop "If" prefixes or join with "and" inline).
 */

import { nullableRequiredWarning } from './branchNullability';

const OPERATOR_PHRASES = {
	const: { verb: 'equals' },
	enum: { verb: 'is one of' },
	not_in: { verb: 'is not one of' },
	minimum: { verb: 'is at least' },
	maximum: { verb: 'is at most' },
	exclusive_minimum: { verb: 'is greater than' },
	exclusive_maximum: { verb: 'is less than' },
	contains_const: { verb: 'contains' },
	type_is_null: { verb: 'is empty', valueless: true },
};

function formatValue(value) {
	if (Array.isArray(value)) return value.map(String).join(', ');
	if (value == null) return '';
	const s = String(value);
	if (s === '') return '""';
	return s;
}

function describeCondition(cond) {
	const op = OPERATOR_PHRASES[cond?.operator] || { verb: cond?.operator || 'matches' };
	if (op.valueless) {
		return `${cond.field || '(unspecified field)'} ${op.verb}`;
	}
	return `${cond.field || '(unspecified field)'} ${op.verb} ${formatValue(cond.value)}`;
}

function describeConditions(ifConditions, negated) {
	const parts = (ifConditions || [])
		.filter((c) => c && (c.field || c.operator))
		.map(describeCondition);
	if (parts.length === 0) return negated ? 'Unless (no condition set)' : 'If (no condition set)';
	const joined = parts.join(' and ');
	return negated ? `Unless ${joined}` : `If ${joined}`;
}

// An "at least one of" group is a single constraint over several fields, so it
// reads best as one sentence ("at least one of a, b must be provided"). Beyond
// this many names the sentence stops scanning, and we fall back to the same
// headline + list shape the multi-field required / not-allowed cases use.
const AT_LEAST_ONE_INLINE_MAX = 3;

function describeRequiredness(fields) {
	const prohibited = fields.filter((f) => f && f.fieldName && f.prohibited);
	// `atLeastOne` rows carry no per-field requiredness — they compile to the
	// branch's `anyOf` group — so they must stay out of the required/optional
	// buckets, which would otherwise mislabel them as "optional".
	const atLeastOne = fields.filter((f) => f && f.fieldName && f.atLeastOne && !f.prohibited);
	const required = fields.filter((f) => f && f.required && !f.prohibited && !f.atLeastOne);
	const optional = fields.filter(
		(f) => f && f.fieldName && !f.required && !f.prohibited && !f.atLeastOne,
	);
	const onlyAtLeastOne = atLeastOne.length > 0
		&& required.length === 0 && optional.length === 0 && prohibited.length === 0;
	const noAtLeastOne = atLeastOne.length === 0;

	if (required.length === 0 && optional.length === 0
		&& prohibited.length === 0 && atLeastOne.length === 0) return null;

	// Most rules carry a single field — the common cases.
	if (required.length === 1 && optional.length === 0 && prohibited.length === 0 && noAtLeastOne) {
		return { kind: 'single', text: `${required[0].fieldName} is required` };
	}
	if (required.length === 0 && optional.length === 1 && prohibited.length === 0 && noAtLeastOne) {
		return { kind: 'single', text: `${optional[0].fieldName} is optional` };
	}
	if (required.length === 0 && optional.length === 0 && prohibited.length === 1 && noAtLeastOne) {
		return { kind: 'single', text: `${prohibited[0].fieldName} must not be provided` };
	}
	if (onlyAtLeastOne && atLeastOne.length <= AT_LEAST_ONE_INLINE_MAX) {
		return {
			kind: 'single',
			text: `at least one of ${atLeastOne.map((f) => f.fieldName).join(', ')} must be provided`,
		};
	}

	// Multi-field, uniform — show as a bulleted-style headline + list.
	if (required.length > 1 && optional.length === 0 && prohibited.length === 0 && noAtLeastOne) {
		return {
			kind: 'list',
			headline: `${required.length} fields are required:`,
			fields: required.map((f) => f.fieldName),
		};
	}
	if (prohibited.length > 1 && required.length === 0 && optional.length === 0 && noAtLeastOne) {
		return {
			kind: 'list',
			headline: `${prohibited.length} fields must not be provided:`,
			fields: prohibited.map((f) => f.fieldName),
		};
	}
	if (onlyAtLeastOne) {
		return {
			kind: 'list',
			headline: 'at least one of these fields must be provided:',
			fields: atLeastOne.map((f) => f.fieldName),
		};
	}

	// Mixed: prefer required count for the headline; surface optional,
	// at-least-one and not-allowed fields in secondary clauses.
	const headlineParts = [];
	if (required.length > 0) {
		headlineParts.push(`${required.length} required`);
	}
	if (optional.length > 0) {
		headlineParts.push(`${optional.length} optional`);
	}
	if (atLeastOne.length > 0) {
		headlineParts.push(`${atLeastOne.length} at least one`);
	}
	if (prohibited.length > 0) {
		headlineParts.push(`${prohibited.length} not allowed`);
	}
	return {
		kind: 'list',
		headline: `${headlineParts.join(' + ')} fields:`,
		fields: [
			...required.map((f) => `${f.fieldName} (required)`),
			...optional.map((f) => `${f.fieldName} (optional)`),
			...atLeastOne.map((f) => `${f.fieldName} (at least one)`),
			...prohibited.map((f) => `${f.fieldName} (not allowed)`),
		],
	};
}

export function compileRuleNarrative(rule) {
	const condition = describeConditions(rule?.ifConditions, !!rule?.negated);
	const thenPart = describeRequiredness(rule?.thenFields || []);
	const elsePart = describeRequiredness(rule?.elseFields || []);

	return {
		condition,
		then: thenPart,
		elsePart,
	};
}

/**
 * Flatten the narrative object into a single one-line sentence — useful for
 * the collapsed headline.
 */
export function compileRuleHeadline(rule) {
	const { condition, then, elsePart } = compileRuleNarrative(rule);
	const segments = [condition];
	if (then) {
		if (then.kind === 'single') {
			segments.push(`then ${then.text}`);
		} else {
			segments.push(`then ${then.headline} ${then.fields.slice(0, 3).join(', ')}${then.fields.length > 3 ? ', …' : ''}`);
		}
	} else {
		segments.push('then (no constraint set)');
	}
	if (elsePart) {
		if (elsePart.kind === 'single') {
			segments.push(`else ${elsePart.text}`);
		} else {
			segments.push(`else ${elsePart.headline} ${elsePart.fields.slice(0, 3).join(', ')}${elsePart.fields.length > 3 ? ', …' : ''}`);
		}
	}
	return segments.join(' ');
}

/**
 * Pluck the set of fieldNames referenced by a rule's IF / THEN / ELSE
 * clauses. Used by the "Conditional" badge generator on the per-field
 * view panel.
 */
export function fieldsReferencedByRule(rule) {
	const out = new Set();
	for (const c of rule?.ifConditions || []) {
		if (c?.field) out.add(c.field);
	}
	for (const f of rule?.thenFields || []) {
		if (f?.fieldName) out.add(f.fieldName);
	}
	for (const f of rule?.elseFields || []) {
		if (f?.fieldName) out.add(f.fieldName);
	}
	return out;
}

/**
 * Build a Map<fieldName, ruleIndex[]> so a row can show "Conditional —
 * referenced in Rule 1, Rule 3" without re-walking every rule per field.
 */
export function buildFieldToRulesIndex(rules) {
	const index = new Map();
	(rules || []).forEach((rule, ruleIdx) => {
		for (const name of fieldsReferencedByRule(rule)) {
			if (!index.has(name)) index.set(name, []);
			index.get(name).push(ruleIdx);
		}
	});
	return index;
}

/**
 * Does this branch row state a type of its own? A stated non-empty `type` makes
 * `emitFieldConstraint` write a `properties: { name: { type: ... } }` pin into
 * the branch, and branch subschemas are conjoined with the root `properties` —
 * so the pin decides the type inside the branch and a widened root type is
 * inert there.
 */
function statesOwnType(field) {
	return typeof field.type === 'string' && field.type.trim() !== '';
}

/**
 * Which fields will this rule emit into a branch's `required` array — NOT which
 * fields the rule mentions. Returns `Map<fieldName, ('then'|'else')[]>`.
 *
 * Deliberately a second walker rather than an option on
 * `fieldsReferencedByRule`: that one answers "referenced", and a discriminator
 * IS referenced. This one answers the much narrower question the DSV-017
 * advisory needs, so the two cannot be confused at a call site.
 *
 * `ifConditions` is never read. A row is included only when EVERY one of these
 * holds, each mirroring what `emitBranch` (serialization.js) actually writes:
 *  - not `prohibited` — the row emits no `properties` entry at all, only a
 *    `not: { anyOf: [{ required: [name] }] }`, so key PRESENCE rejects there.
 *  - not `atLeastOne` — the row emits an `anyOf` arm, which `buildAtLeastOneArm`
 *    already type-pins so it cannot be satisfied by an explicit null.
 *  - `required !== false`, strictly — an ABSENT `required` means required to
 *    `emitBranch` (`requireLeaf: f.required !== false`) and to
 *    `subtractExcludedFromRequired`, so a truthiness test here would drop rows
 *    the emitter treats as mandatory.
 *  - the row states no type of its own (see `statesOwnType`), which covers a
 *    scalar/array pin and a deliberate `type: 'null'` branch in one condition.
 *  - `nullableRequiredWarning` is silent for it — a `nullableInBranch` +
 *    required row is already surfaced by REQUIRED_NULLABLE_WARNING and must not
 *    be reported twice.
 *
 * `synthesizedFromRequired` stubs are INCLUDED, unlike in
 * `getBranchFieldWarnings`' exclusion list: `emitBranch` skips their
 * `properties` entirely and the name still reaches `required` via
 * `thenRequiredOrder` / `elseRequiredOrder`, so they inherit the widened root
 * type with certainty. The two exclusion lists are not interchangeable.
 */
export function fieldsRequiredByRuleBranches(rule) {
	const out = new Map();
	for (const [branch, rows] of [['then', rule?.thenFields], ['else', rule?.elseFields]]) {
		for (const f of rows || []) {
			if (!f || typeof f.fieldName !== 'string' || f.fieldName === '') continue;
			if (f.prohibited || f.atLeastOne) continue;
			if (f.required === false) continue;
			if (statesOwnType(f)) continue;
			if (nullableRequiredWarning(f) !== null) continue;
			const branches = out.get(f.fieldName);
			if (!branches) {
				out.set(f.fieldName, [branch]);
			} else if (!branches.includes(branch)) {
				branches.push(branch);
			}
		}
	}
	return out;
}

/**
 * Build a Map<fieldName, { ruleIdx, branches }[]> over
 * `fieldsRequiredByRuleBranches`, so the DSV-017 advisory can name both the
 * rules and the branches without re-walking every rule per field.
 */
export function buildFieldToRequiringRulesIndex(rules) {
	const index = new Map();
	(rules || []).forEach((rule, ruleIdx) => {
		for (const [name, branches] of fieldsRequiredByRuleBranches(rule)) {
			if (!index.has(name)) index.set(name, []);
			index.get(name).push({ ruleIdx, branches });
		}
	});
	return index;
}
